import { AxiosService } from '../../axios/axios.service';
import {
  tChatDaddyConnectionResponse,
  tCreateChatDaddyAccountRequest,
  tCreateChatDaddyAccountResponse,
  tGenerateQrCodeRequest,
  tGenerateQrCodeResponse,
  tGetAccountStatusRequest,
  tGetAccountStatusResponse,
  tGetConversationsFromChatDaddyRequest,
  tGetConversationsFromChatDaddyResponse,
  tGetFacebookPagesRequest,
  tGetMetaAccountIdResponse,
  tGetMetaConversationsRequest,
  tGetMetaConversationsResponse,
  tGetMetaPagesResponse,
  tIntegrationsRequest,
  tIntegrationsResponse,
  tSelectPageRequest,
  tSelectPageResponse,
  tSendBulkMessagesRequest,
  tSendBulkMessagesResponse,
  tSendBulkMessagesToMetaRequest,
  tSendBulkMessageToMetasResponse,
  tSendUpdateAccountToNoMessagesHistoryData,
  tSendUpdateAccountToNoMessagesHistoryResponse,
} from './Integrations.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

/**
 * Per-call timeout for the WhatsApp Web account-status probe (getAccountStatus).
 * Overrides the 30s global axios ceiling (see axios.singleton.ts): the status
 * check is a fast liveness probe, and during a whatsapp-web-service degradation
 * a 30s hang would tie up a browser connection slot per open chat. 8s is enough
 * to distinguish a slow dependency from a missing one. (INT-941 review.)
 */
const ACCOUNT_STATUS_TIMEOUT_MS = 8_000;

/**
 * Endpoint used to close the connection with Chat Daddy. This does NOT delete the Chat Daddy account
 */

export const IntegrationsService = {
  /*
   * Endpoint used to close the Chat Daddy session for a given workspace
   * @param {string} workspaceID - The workspace ID for which the session is being closed
   * @returns {Promise<void>} - A promise that resolves when the session is successfully closed
   * @throws {Error} - If the request fails
   */
  closeChatDaddySession: async (workspaceID: string): Promise<void> => {
    try {
      const config = {
        headers: {},
        isLegacy: true,
      };
      await AxiosService.post<object, tChatDaddyConnectionResponse>(
        `/whatsapp-web/account/${workspaceID}/close`,
        {},
        config,
      );
    } catch (err) {
      return LLog.error(
        {
          component: LOG_COMPONENTS.INTEGRATIONS,
          method: 'Integrations.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        `Error closing session and API Key`,
        err as unknown as Error,
      );
    }
  },
  /*
   * Endpoint used to get the integrations for a given workspace
   * @param {tIntegrationsRequest} request - The request object containing the workspace ID
   * @returns {Promise<tIntegrationsResponse>} - A promise that resolves to the response object
   * @throws {Error} - If the request fails
   */
  getIntegrations: async function (
    request: tIntegrationsRequest,
  ): Promise<tIntegrationsResponse> {
    try {
      const response = await AxiosService.get<tIntegrationsResponse>(
        `/workspace/${request.uuid}/integrations`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to create a Chat Daddy account for a given workspace
   * @param {tCreateChatDaddyAccountRequest} request - The request object containing the workspace ID
   * @returns {Promise<tCreateChatDaddyAccountResponse>} - The creation of a new account on the chatDaddy platform
   * @throws {Error} - If the request fails
   *
   */
  createChatDaddyAccount: async (
    request: tCreateChatDaddyAccountRequest,
  ): Promise<tCreateChatDaddyAccountResponse> => {
    try {
      const response = await AxiosService.post<
        object,
        tCreateChatDaddyAccountResponse
      >(`/whatsapp-web/account`, request, {
        headers: {},
        isLegacy: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to generate a QR code for a given workspace
   * @param {tGenerateQrCodeRequest} request - The request object containing the workspace ID
   * @returns {Promise<tGenerateQrCodeResponse>} - generates de QR code but its return on the websockets
   * @throws {Error} - If the request fails
   *
   */
  generateQrCode: async function (
    request: tGenerateQrCodeRequest,
  ): Promise<tGenerateQrCodeResponse> {
    try {
      const response = await AxiosService.get<tGenerateQrCodeResponse>(
        `/whatsapp-web/account/${request.workspaceId}/qr`,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to get the account status for a given workspace
   * @param {tGetAccountStatusRequest} request - The request object containing the workspace ID
   * @returns {Promise<tGetAccountStatusResponse>} -the status of the account tGetAccountStatusResponse
   * @throws {Error} - If the request fails
   */
  getAccountStatus: async function (request: tGetAccountStatusRequest) {
    try {
      const response = await AxiosService.get<tGetAccountStatusResponse>(
        `/whatsapp-web/account/${request.workspaceId}/state`,
        {
          headers: {},
          isLegacy: true,
          timeout: ACCOUNT_STATUS_TIMEOUT_MS,
        },
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to get the conversations from Chat Daddy
   * @param {tGetConversationsFromChatDaddyRequest} request - The request object containing the workspace ID
   * @returns {Promise<tGetConversationsFromChatDaddyResponse>} - The conversations from Chat Daddy
   * @throws {Error} - If the request fails
   */
  getConversationsFromChatDaddy: async function (
    request: tGetConversationsFromChatDaddyRequest,
  ) {
    try {
      const response =
        await AxiosService.get<tGetConversationsFromChatDaddyResponse>(
          `/whatsapp-web/chats/${request.workspaceId}`,
          {
            headers: {},
            isLegacy: true,
          },
        );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to ask to sync the messages with Leadsales - CD
   * @param {tSendBulkMessagesRequest} request - workspaces_id and an array of handles
   * @returns N/A
   */
  sendBulkMessages: async function (request: tSendBulkMessagesRequest) {
    try {
      const response = await AxiosService.post<
        tSendBulkMessagesRequest,
        tSendBulkMessagesResponse
      >(`/whatsapp-web/messages/insert/${request.workspace}`, request, {
        headers: {},
        isLegacy: true,
      });

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used mark as WID already synced
   * @param {string} workspaceId - The workspace ID
   * @returns {Promise<void>} - A promise that resolves when the request is completed
   */
  updateAccountToNoMessagesHistory: async function (workspaceId: string) {
    try {
      const data: tSendUpdateAccountToNoMessagesHistoryData = {
        options: {
          getMessageHistory: false,
        },
      };
      const response = await AxiosService.patch<
        tSendUpdateAccountToNoMessagesHistoryData,
        tSendUpdateAccountToNoMessagesHistoryResponse
      >(`/whatsapp-web/account/${workspaceId}/options`, data, {
        headers: {},
        isLegacy: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to get the Facebook pages for a given workspace
   * @param {tGetFacebookPagesRequest} request - The request object containing the workspace ID
   * @returns {Promise<tGetMetaPagesResponse | null>} The pages, or null when the workspace
   *   has no Meta account connected yet.
   */
  getFacebookPages: async function (request: tGetFacebookPagesRequest) {
    try {
      const metaAccount = await AxiosService.get<tGetMetaAccountIdResponse>(
        `/meta-messenger/meta-account/${request.workspaceId}`,
        {
          headers: {},
          isLegacy: true,
        },
      );

      // A workspace with no Meta account connected answers `{"metaAccountId":""}` — a 200,
      // not an error. Interpolating that built a request to `/meta-messenger/all-pages/`
      // with no id at all, which is not a route anywhere: the gateway does not publish it,
      // so the browser received a 404 carrying no CORS headers and reported it as a failed
      // request ("Provisional headers are shown") rather than a readable status. Under the
      // old gateway the same call reached the service and was swallowed by the catch below,
      // so it was always pointless — just quieter.
      if (!metaAccount?.metaAccountId) {
        return Promise.resolve(null);
      }

      const response = await AxiosService.get<tGetMetaPagesResponse>(
        `/meta-messenger/all-pages/${metaAccount.metaAccountId}`,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to select a Facebook page by the UUID of the page
   * @param {tSelectPageRequest} request - The request object containing the page ID
   * @returns {Promise<object>} - The response object
   * @throws {Error} - If the request fails
   */
  selectFacebookPage: async function (request: tSelectPageRequest) {
    try {
      const response = await AxiosService.put<
        tSelectPageRequest,
        tSelectPageResponse
      >(`/meta-messenger/select-page?uuid=${request.pageId}`, request, {
        headers: {},
        isLegacy: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint to get all the conversations from a given page
   * @param {string} pageId - The UUID of the page
   * @returns {Promise<object>} - The response object
   * @throws {Error} - If the request fails
   */
  getMetaConversations: async function (request: tGetMetaConversationsRequest) {
    try {
      const response = await AxiosService.get<tGetMetaConversationsResponse>(
        `/meta-messenger/conversations/${request.pageUuid}/${request.type}`,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to send bulk messages to Meta
   * @param {tSendBulkMessagesToMetaRequest} request - The request object containing the workspace ID and the conversations
   * @returns {Promise<object>} - The response object
   * @throws {Error} - If the request
   */
  sendBulkMessagesToMeta: async function (
    request: tSendBulkMessagesToMetaRequest,
  ) {
    try {
      const response = await AxiosService.post<
        tSendBulkMessagesToMetaRequest,
        tSendBulkMessageToMetasResponse
      >(`/meta-messenger/messages/insert/${request.workspaceId}`, request, {
        headers: {},
        isLegacy: true,
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /*
   * Endpoint used to delete an integration state
   * @param {string} userId - The user ID
   * @param {string} workspaceId - The workspace ID
   */
  disconnectAndUnsubscribe: async function (pageId: string) {
    try {
      const response = await AxiosService.delete(
        `/meta-messenger/unsubscribe-page?uuid=${pageId}`,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /*
   * Endpoint used to delete chat daddy account and whatsapp integration state
   * @param {string} userId - The user ID
   * @param {string} workspaceId - The workspace ID
   */
  deleteAccount: async function (wid: string) {
    try {
      const response = await AxiosService.delete(
        `/whatsapp-web/account/${wid}`,
        {
          headers: {},
          isLegacy: true,
        },
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
